import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/opt/render/project/src/src/components/layout.js";
import { Link } from 'gatsby';
import Layout from '../components/layout';
import SEO from '../components/seo';
import hero from "../images/lab/hero.webp";
import lab from "../images/lab/lab.webp";
import sorting from "../images/lab/sorting.webp";
import working from "../images/lab/working.webp";
import evangelion from "../images/lab/evangelion.webp";
import kinesis from "../images/lab/kinesis.webp";
import channels from "../images/lab/channels.webp";
import channel from "../images/lab/channel.webp";
import spinner from "../images/lab/spinner.webp";
import mcs1 from "../images/lab/mcs1.webp";
import spinrender from "../images/lab/spinrender.webp";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <SEO title="Microbiology Lab" keywords={['Seraph', 'kumar', 'designer', 'user', 'experience', 'product', 'interaction', 'gatsby', 'application', 'react']} mdxType="SEO" />
    <img height="712px" loading="eager" src={hero} alt="Mockup of scientific software." className="hero" />  <br />  <br />
    <h1>{`Designing for Scientists`}</h1>
    <h2>{`Microbiology lab`}</h2>
    <p>{`In the Department of Natural Science at California State University San Bernardino, there's a tiny lab doing big things.
With funding from NASA & NIS, a dozen scientists are researching extremophile archaea (microbes) to uncover the origins of humanity.`}</p>
    <img loading="lazy" src={lab} alt="Our lab." className="center" />
    <div className="caption">The main lab.</div>
    <h2>{`Seraph`}</h2>
    <p>{`My duty was to design software tools, hardware equipment, and the entire lab itself. The challenge was to
improve the UX and efficiency of expensive custom-built bleeding-edge systems used by our own scientists.`}</p>
    <p>{`Every project had different teams. I worked with engineers, chemists, carpenters, biophysicists and some other ists.
I was the only designer. `}</p>
    <img height="600px" loading="lazy" src={sorting} alt="Cell-sorting system." className="center" />
    <div className="caption">Most of my work was related to this cell-sorting system.</div>
    <h2>{`Becoming the user`}</h2>
    <p>{`I had to deliver fast. Which meant I had to understand my brilliant users and their dense ever-evolving intricate work fast. Card-sorting sessions just weren't going to
cut it. It only made sense for me to work as a scientist so that I could efficiently learn what I needed to.`}</p>
    <p>{`And hey, I could finally put my expensive Bioinformatics degree to use.`}</p>
    <img height="auto" loading="lazy" src={working} alt="Seraph at work." className="center" />
    <div className="caption">Getting my hands dirty.</div>
    <h2>{`Researching the researchers`}</h2>
    <p>{`One of my favorite ways to understand users is to have them teach and demonstrate their work to me (aka contextual inquiry). When my users taught me how to work alongside them on their
research, they revealed the inner workings of their minds. Asking neutral questions on how to use tools can easily get them to open up about their pain-points. `}</p>
    <p>{`The scientists' understanding of their tools was limited. If I asked them to explain the organs of our cell-sorting system, they'd respond "I don't know".
This was an issue.`}</p>
    <h2>{`Killing spirits`}</h2>
    <p>{`You would not believe how often equipment breaks down at the lab. That's the curse of high-tech custom-built tools. `}</p>
    <p>{`It's on scientists to fix their tools fast and get on with their research. Or else their fussy microbes will die, wasting months
of work and thousands in funding. It's high stakes. I've seen my users furiously bang on keyboards, moan in pain, and look down in despair when
their tools break. How can they fix things they don't understand?`}</p>
    <img height="auto" loading="lazy" src={channels} alt="Microscopic channels." className="center" />
    <div className="caption">I designed microscopic mechanisms to be more durable than before.  <br /> Say hi to my reflection! </div>
    <h2>{`Transparency`}</h2>
    <p>{`Luckily, I watch anime. An amazing sci-fi show called Neon Genesis Evangelion gave me the answer on a silver platter. Technology in this show
had self-explanatory designs. The inner-workings are laid out in diagrams, and status changes are communicated
by interesting animations. This is done so that the audience can understand how sci-fi tech works in a split-second. `}</p>
    <img height="auto" loading="lazy" src={evangelion} alt="Diagrams in Neon Genesis Evangelion" className="center" />
    <div className="caption">This was the scene from Evangelion that made it all click.</div>
    <h2>{`Consider this`}</h2>
    <p>{`Implementing a philosophy of show & tell was difficult. Turning high-tech system guts into a digestible diagram took a lot of research and head-scratching.
I had to account for:`}</p>
    <ul>
      <li parentName="ul">{`User's location in the lab relative to the equipment.`}</li>
      <li parentName="ul">{`Placement of the tools in the lab.`}</li>
      <li parentName="ul">{`Equipment that wasn't willing to communicate its status.`}</li>
      <li parentName="ul">{`Not just how a tool works immediately, but also how it works in greater systems.`}</li>
      <li parentName="ul">{`Users with color-blindness and visual impairments.`}</li>
      <li parentName="ul">{`Power usage of the diagrams (because of inefficient hardware polling).`}</li>
      <li parentName="ul">{`The possibility that replacing parts can deprecate the design.`}</li>
      <li parentName="ul">{`Different phases of research, including preparation and clean-up.`}</li>
    </ul>
    <p>{`And of course, I had to account for designing each product within a month.`}</p>
    <img height="auto" loading="lazy" src={kinesis} alt="Screenshot of Kinesis S" className="center" />
    <div className="caption">Kinesis S (laser controller software) during development.  <br /> It shows that two links are disconnected.</div>
    <img height="auto" loading="lazy" src={spinner} alt="Sketch of my spin-coater" className="center" />
    <div className="caption">My spin-coater lays out all the details of every phase.  <br />Typical spin-coaters don't do this.</div>
    <img height="auto" loading="lazy" src={spinrender} alt="My spin-coater" className="center" />
    <div className="caption">Model and render by <a target="_blank" rel="noopener noreferrer" href="https://www.calum.design">Calum Mills -></a></div>
    <img height="600px" loading="lazy" src={mcs1} alt="My microfluidic chip" className="center" />
    <div className="caption">A computer image of my microfluidic chip.  <br /> MCS-1 reduced errors by indicating faces and ports.</div>
    <h2>{`Good results`}</h2>
    <p>{`Our products were very successful. Once in a while, our scientists would stop by to thank me for my work. My designs were able to shave off
hours of research labor. Scientists actually got to go home earlier because of me.`}</p>
    <p>{`Single-cell sorting times were reduced by 40%. My ergonomic designs reduced hand pains and constant repositioning. Scientists
could finally fix their equipment and save their microbes because of my show & tell philosophy. `}</p>
    <p>{`Since I was delivering good results, my budget & scope increased with every project.`}</p>
    <img height="600px" loading="lazy" src={channel} alt="A nanoscopic channel" className="center" />
    <div className="caption">A photo of a microscopic channel I designed and made.  <br />This one is actually deformed...</div>
    <h2>{`The end`}</h2>
    <p>{`Due to the pandemic, the lab went into hiatus in 2020. Thankfully, I had just designed the final tweaks in our microfluidic chips before the lab
closed.`}</p>
    <p>{`I loved working there. This page was originally a massive essay on all the things I did and learned there. I couldn't stop writing. But all good things
know when to end.`}</p>
    <Link to="/" mdxType="Link">Home</Link>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      